import EditDeleteAddressTypes from '../../../enums/EditDeleteAddressTypes';
import DeliveryCountries from '../../components/address/AddressStartDeliveryCountries';
import {getSelectedAddressCategory} from '../../selectors/selectors';
import store from '../../store/store';
import doMetric from '../../tracking/bigQueryMetricRecorder';
import {
  addDeliveryAddress,
  resetDeliveryAddresses, resetEditedAddress, selectDeliveryAddress, showSpinner,
} from '../address-actions';
import * as CheckoutAPIActions from '../APIActions/CheckoutAPIActions';
import {getDeliveryOptions, resetDeliveryOptions} from '../deliveryOptionActions';
import {showOverlay} from '../overlay-actions';
import {removePageMessage, setPageMessage} from '../pageMessageActions';
import {TooltipAndOverlayIDs} from '../../components/detailed-tooltip/TooltipLibrary';
import Translation from '../../lang/Translation';
import DomUtils from '../../utils/DomUtils';
import Track from '../../utils/tracking';
import setAddressAsEdited from './setAddressAsEdited';

const getData = (fieldValues, deliveryCountry) => ({
  addressee: fieldValues.addressee,
  buildingNameNumber: fieldValues.nameNumber,
  organisationName: fieldValues.companyName,
  line1: fieldValues.streetName,
  line2: fieldValues.addressLine2,
  line3: fieldValues.townCity,
  line4: fieldValues.county,
  postcode: fieldValues.postCode,
  countryCode: deliveryCountry || fieldValues.countryCode,
  addressNickname: fieldValues.addressNickname,
  defaultDeliveryAddress: fieldValues.defaultDeliveryAddress,
  defaultBillingAddress: fieldValues.defaultBillingAddress,
  selectedAddressCategory: fieldValues.selectedAddressCategory,
  uuid: fieldValues.uuid,
});

const mapResponseToFieldNames = response => ({
  addressId: response.legacyId,
  addressType: response.addressType,
  addressee: response.addressee,
  nameNumber: response.buildingNameNumber,
  contactNumber: response.contactNumber,
  country: response.countryCode,
  countryName: DeliveryCountries.getByCountryCode(response.countryCode),
  legacyId: '',
  streetName: response.line1,
  addressLine2: response.line2,
  townCity: response.line3,
  county: response.line4,
  companyName: response.organisationName,
  postCode: response.postcode,
  residentId: response.residentId,
  addressNickname: response.addressNickname,
  selectedAddressCategory: response.selectedAddressCategory || 'STORED_RESIDENTIAL_ADDRESS',
  defaultDeliveryAddress: response.defaultDeliveryAddress,
  defaultBillingAddress: response.defaultBillingAddress,
  uuid: response.uuid,
});

const hasPageMessage = () => store.getState().pageMessage.message
  === (Translation.getText('checkout.delivery.editAddress.overlay.pageMessage')
    || Translation.getText('checkout.delivery.deleteAddress.overlay.pageMessage'));

const editAddress = (fieldValues, deliveryCountry) => (dispatch) => {
  if (hasPageMessage()) {
    dispatch(removePageMessage());
  }
  dispatch(showSpinner(true));
  dispatch(showOverlay(TooltipAndOverlayIDs.EDIT_DELETE_ADDRESS_SUCCESS, {
    type: EditDeleteAddressTypes.EDIT_ADDRESS,
  }));
  
  const data = getData(fieldValues, deliveryCountry);
  
  dispatch(CheckoutAPIActions.editAddress(fieldValues.addressId, data))
    .then((response) => {
      if (typeof response !== 'undefined') {
        if (response.msg?.length > 0) {
          dispatch(setAddressAsEdited(fieldValues.addressId));
          dispatch(resetDeliveryAddresses(deliveryCountry));
          dispatch(resetEditedAddress());
          response.msg.forEach((address) => {
            // Response will return addressId in new format, so need to use legacyId here
            dispatch(addDeliveryAddress(address.legacyId, mapResponseToFieldNames(address)));
          });
          dispatch(selectDeliveryAddress(fieldValues.addressId, fieldValues.selectedAddressCategory));
          dispatch(getDeliveryOptions());
          dispatch(showSpinner(false));
    
          try {
            doMetric('editAddress', [{name: 'success', value: 'true'}]);
          } catch (error) {
            Track.track(error);
          }
        } else {
          dispatch(showSpinner(false));
          dispatch(setPageMessage(Translation.getText('checkout.delivery.editAddress.overlay.pageMessage')));
          Track.track('Error with editAddress endpoint - response is empty');
        }
      } else {
        Track.track('Error with editAddress endpoint - no response');
      }
    })
    .catch((error) => {
      dispatch(showSpinner(false));
      dispatch(setPageMessage(Translation.getText('checkout.delivery.editAddress.overlay.pageMessage')));
      DomUtils.closeOverlayIfClosable();
      Track.track(error);
  
      try {
        doMetric('editAddress', [{name: 'success', value: 'false'}]);
      } catch (err) {
        Track.track(err);
      }
    });
};

const deleteAddress = (addressId, deliveryCountry) => (dispatch, getState) => {
  if (hasPageMessage()) {
    dispatch(removePageMessage());
  }
  dispatch(showSpinner(true));
  dispatch(showOverlay(TooltipAndOverlayIDs.EDIT_DELETE_ADDRESS_SUCCESS, {
    type: EditDeleteAddressTypes.DELETE_ADDRESS,
  }));
  
  dispatch(CheckoutAPIActions.deleteAddress(addressId))
    .then((response) => {
      if (typeof response.msg !== 'undefined') {
        dispatch(resetDeliveryAddresses(deliveryCountry));
        if (response.msg?.length > 0) {
          response.msg.forEach((address) => {
            // Response will return addressId in new format, so need to use legacyId here
            dispatch(addDeliveryAddress(address.legacyId, mapResponseToFieldNames(address)));
          });
        }
        dispatch(resetDeliveryOptions());
        if (response.msg?.length > 0) {
          // Response will return addressId in new format, so need to use legacyId here
          dispatch(selectDeliveryAddress(response.msg[0].legacyId, getSelectedAddressCategory(getState())));
          dispatch(getDeliveryOptions());
        }
        dispatch(showSpinner(false));
      } else {
        dispatch(showSpinner(false));
        dispatch(setPageMessage(Translation.getText('checkout.delivery.editAddress.overlay.pageMessage')));
        Track.track('Error with deleteAddress endpoint - response is empty');
      }
      
      try {
        doMetric('deleteAddress', [{name: 'success', value: 'true'}]);
      } catch (error) {
        Track.track(error);
      }
    })
    .catch((error) => {
      dispatch(showSpinner(false));
      dispatch(setPageMessage(Translation.getText('checkout.delivery.deleteAddress.overlay.pageMessage')));
      DomUtils.closeOverlayIfClosable();
      Track.track(error);
      
      try {
        doMetric('deleteAddress', [{name: 'success', value: 'false'}]);
      } catch (err) {
        Track.track(err);
      }
    });
};

const editDeleteAddressActions = {
  editAddress,
  deleteAddress,
};

export default editDeleteAddressActions;
